import { act, useEffect, useMemo, useRef, useState } from "react"
import { ReactFlow, MiniMap, Background } from "@xyflow/react"
import { Divider, IconButton } from "@mui/material"
import Save from "@mui/icons-material/Save"
import Search from "@mui/icons-material/Search"
import WarningIcon from '@mui/icons-material/Warning'
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import { useReactFlow } from "@xyflow/react"
import NodeContext from "../../context/node-value.context"
import FlowAutoComplete from "../../components/flowautocomplete/flowautocomplete"
import useInitAutocompleteSelect from "../../hooks/useInitAutocompleteSelect"
import CustomNode from "../../components/flownodes/custom-node.component"
import useXYFlow from "../../hooks/useXYFlow"
import { formatEnvURL } from "../../util/env"
import { getIO } from "../../util/blueprint"
import { areObjectsDifferent } from '../../util/util'
import { saveProject, saveSnapshot } from '../../api-client/data'

import { useSelector, useTrigger, Triggers, Actions, useDispatch, useRegisterTrigger } from "../../stores/dashboard.store"
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from "../snackbar/snackbar"

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import styles from "../../styles/blueprint.module.css"
import { uuidv4 } from "../../util/hash"
import useRenderElement from "../../hooks/useRenderElement"
import SetDefault from "../dialogs/set-default"
import Tooltip from "../tooltip"

const ReactFlowContextProvider = ({ setZoomIn, setZoomOut }) => {
    const flowInstance = useReactFlow()

    useRegisterTrigger(Triggers.GetReactFlowViewport, flowInstance.getViewport)

    useEffect(() => {
        setZoomIn(() => flowInstance.zoomIn)
        setZoomOut(() => flowInstance.zoomOut)
    }, [flowInstance, setZoomIn, setZoomOut])

    return null
}

const BlueprintActionButton = ({tooltip, onClick, children, show}) => {
    if (show === false) return null

    return (
        <Tooltip title={tooltip}>
            <IconButton onClick={onClick}>
                {children}
            </IconButton>
        </Tooltip>
    )
}

const verifyDefaultValues = (nodes, defaultValues = {}) => {
    const inputs = nodes?.reduce((a,c) => {
        const e = c?.data?.inputs.reduce((aa, cc) => {
            aa[cc?.id] = true

            return aa
        }, {})

        return {
            ...a,
            ...e,
        }
    }, {})

    return Object.keys(defaultValues).filter(x=>inputs[x]===true).reduce((a,c) => {
        a[c] = defaultValues[c]
        return a
    }, {})
}

export default function Blueprint() {
    const { activeProject, activeProjectRequiresSave, customNodeNames, tasks, loadingProjectId, unfulfilledNodes, theme } = useSelector()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const xyflow = useXYFlow()
    const autocomplete = useInitAutocompleteSelect(xyflow.setNodes, xyflow.nodes)
    const [defaultValueData, setDefaultValueData] = useState()

    const [didDisplaySnackbar, setDidDisplaySnackbar] = useState(false)
    const [lastState, setLastState] = useState()
    const [zoomIn, setZoomIn] = useState(null)
    const [zoomOut, setZoomOut] = useState(null)

    const compileStatusRefresh = useTrigger(Triggers.CompileStatus)
    const setActiveProject = useTrigger(Triggers.SetActiveProject)
    const renderReactFlow = useRenderElement()

    const [defaultValues, setDefaultValues] = useState(activeProject?.defaultValues)
    
    useEffect(() => {
        // get cached node, if it is different, set info
        const lastBlueprintId = localStorage.getItem("blueprintId")
        if (lastBlueprintId === activeProject?.id && activeProject?.nodes === 0) {
            return
        }

        xyflow.setNodes(activeProject?.nodes || [])
        xyflow.setEdges(activeProject?.edges || [])
    }, [])

    const getServerNodes = async () => {
        const res = await fetch(formatEnvURL("config/nodes"))
        const jsonRes = await res.json()

        autocomplete.setAutoCorrectNodes(jsonRes)
    }

    useEffect(() => {
        getServerNodes()
    }, [])

    const prevEdgeCount = useRef(null)

    const saveBlueprint = async () => {
        dispatch(Actions.SetLoadingProjectId, activeProject?.id)
        enqueueSnackbar("Saving Blueprint", { variant: "info" })

        const nodes = xyflow.nodes.map(x => ({ ...x, data: { ...x.data, name: customNodeNames[x?.id] || x?.data?.name } }))
        const io = getIO(nodes, xyflow.edges)
        const isIODifferent = areObjectsDifferent(activeProject?.io, io)

        const flowImage = await renderReactFlow.handleExport()

        // saves the snapshot to the project
        await saveSnapshot({ id: activeProject?.id, snapshot: flowImage })

        const updatedDefaultValues =  verifyDefaultValues(xyflow.nodes, defaultValues)
        const updatedProject = {
            data: {
                io,
                edges: xyflow.edges,
                nodes: nodes,
            },
            subProjects: xyflow.nodes.filter(x => !!x?.data?.projectId).map(x => ({ projectId: x?.data?.projectId, parentProjectVersion: x?.data?.version })),
            id: activeProject?.id,
            version: isIODifferent ? uuidv4() : activeProject?.version || uuidv4(),
            isSmart: !!nodes.find(x => x?.data?.isSmart),
            defaultValues:updatedDefaultValues,
        }

        await saveProject(updatedProject)
        enqueueSnackbar("Saved Blueprint", {
            variant: "success"
        })

        // prevEdgeCount is used when determining if the save button should be active
        // each time the project is saved, we catch the new count to check against
        prevEdgeCount.current = xyflow.edges.length

        compileStatusRefresh()
        dispatch(Actions.SetActiveProject, {
            ...activeProject,
            ...updatedProject?.data,
            version: updatedProject?.version,
            defaultValues: updatedDefaultValues,
        })

        dispatch(Actions.SetLoadingProjectId, undefined)
    }

    useEffect(() => {
        if (lastState === undefined || activeProject?.id !== lastState) {
            setLastState(activeProject?.id)
            return
        }

        if (activeProject?.io === undefined && xyflow.nodes.length === 0) return

        const nodes = xyflow.nodes.map(x => ({ ...x, data: { ...x.data, name: customNodeNames[x?.id] || x?.data?.name } }))

        if (prevEdgeCount.current === null) {
            // we initialize this for the first time
            prevEdgeCount.current = xyflow.edges.length
        }

        // any time a change is detected, we will re-enable the save button
        const io = getIO(nodes, xyflow.edges)

        const isIODifferent = areObjectsDifferent(activeProject?.io, io) || prevEdgeCount.current !== xyflow.edges.length

        dispatch(Actions.SetActiveProjectRequiresSave, isIODifferent)

        if (isIODifferent && lastState === activeProject?.id && !didDisplaySnackbar) {
            enqueueSnackbar("Please save the blueprint before running", {
                variant: "warning",
            })
            setDidDisplaySnackbar(true)
        }
    }, [xyflow.nodes, xyflow.edges, customNodeNames, activeProject])

    const isRunning = useMemo(() => !!tasks[activeProject?.id] || loadingProjectId === activeProject?.id, [tasks, loadingProjectId])
    const disabledSaveButton = useMemo(() => !activeProjectRequiresSave || isRunning, [activeProjectRequiresSave, isRunning])
    const isDeleteActive = useMemo(() => (!!xyflow.activeNodeId || !!xyflow.activeEdgeId) && !isRunning, [isRunning, xyflow.activeEdgeId, xyflow.activeNodeId])
    const hasUnfulfilledNodes = useMemo(() => Object.values(unfulfilledNodes).reduce((a, c) => a + c?.length, 0) > 0, [unfulfilledNodes])

    const showNotificationError = () => {
        if (hasUnfulfilledNodes) {
            dispatch(Actions.SetDashboardError, { title: 'Node Error', description: 'All nodes must has connected inputs' })
        }
    }

    const onConnectEnd = (event, connectionState) => {
        if (connectionState?.isValid) return

        const output = connectionState?.fromNode?.data?.outputs.find(x => x.id === connectionState?.fromHandle?.id)
        const input = connectionState?.fromNode?.data?.inputs.find(x => x.id === connectionState?.fromHandle?.id)

        autocomplete.setShowAutoCompleteFull({ visible: true, data: { type: (output || input).type, to: connectionState?.to, isInput: !!input, isDragged: true } })
    }

    const setNodeDefaultOnClick = () => {
        const activeNode = xyflow.nodes.find(x => x?.id === xyflow.activeNodeId)
        setDefaultValueData(activeNode?.data)
    }

    const isActiveNodeSettable = useMemo(() => {
        if (xyflow.activeNodeId === undefined) return false

        const activeNode = xyflow.nodes.find(x => x?.id === xyflow.activeNodeId)
        return activeNode?.data?.inputs?.some(x => x?.use_as_edge === false)
    }, [xyflow.activeNodeId])

    const onSaveSetDefault = (id, value) => {
        setDefaultValues((prev) => ({
            ...prev,
            [id]: value,
        }))

        setDefaultValueData(undefined)

        dispatch(Actions.SetActiveProjectRequiresSave, true)
    }

    return (
        <div
            style={{
                position: "relative",
                overflow: "hidden",
                width: "100%",
                height: "100%",
                zIndex: 0,
            }}
        >
            {
                <div className={styles.actionsGroup}>
                    <div className={`${styles.innerButtonBar}`}>
                        <BlueprintActionButton tooltip="Search for a node" onClick={() => autocomplete.setShowAutoComplete(true)} show>
                            <Search style={{ cursor: 'pointer', color: "var(--text-primary)" }} />
                        </BlueprintActionButton>
                        
                        <BlueprintActionButton tooltip="Save the current blueprint" onClick={saveBlueprint} show={!disabledSaveButton}>
                            <Save style={{ cursor: 'pointer', color: disabledSaveButton ? "var(--icon-disabled)" : "var(--text-primary)" }} />
                        </BlueprintActionButton>

                        <BlueprintActionButton tooltip="Delete the currently selected edge or node" onClick={xyflow.deleteActiveNodeAndEdge} show={isDeleteActive}>
                            <DeleteIcon style={{ cursor: 'pointer', color: !isDeleteActive ? "var(--icon-disabled)" : "var(--text-primary)" }} />
                        </BlueprintActionButton>
                        
                        <BlueprintActionButton tooltip="Your blueprint has warnings" onClick={showNotificationError} show={hasUnfulfilledNodes}>
                            <WarningIcon style={{ cursor: 'pointer', color: !hasUnfulfilledNodes ? "var(--icon-disabled)" : "rgb(196, 85, 33)" }} />
                        </BlueprintActionButton>

                        <BlueprintActionButton tooltip="Save a default value for the selected node" onClick={setNodeDefaultOnClick} show={isActiveNodeSettable}>
                            <DisplaySettingsIcon style={{ cursor: 'pointer', color: "var(--text-primary)" }} />
                        </BlueprintActionButton>
                    </div>
                </div>
            }


            <div className={`${styles.actionsGroup}`} style={{ position: 'absolute', bottom: 100, left: 140, zIndex: 1 }}>
                <div className={`${styles.innerButtonBar} `}>
                    <IconButton onClick={zoomIn}>
                        <AddIcon style={{ color: "var(--text-primary)" }} />
                    </IconButton>
                    <IconButton onClick={zoomOut}>
                        <RemoveIcon style={{ cursor: 'pointer', color: "var(--text-primary)" }} />
                    </IconButton>
                </div>
            </div>


            {autocomplete.showAutocomplete && (
                <div
                    style={{
                        position: "absolute",
                        width: "100vw",
                        zIndex: 99999,
                        height: "98vh",
                    }}
                >
                    <FlowAutoComplete
                        selectionInfo={autocomplete.showAutocompleteFull?.data}
                        onSelect={autocomplete.onAutocompleteSelect}
                        items={autocomplete.autoCorrectNodes}
                        onClose={() => autocomplete.setShowAutoComplete(false)}
                    />
                </div>
            )}

            <SetDefault
                {...defaultValueData}
                show={!!defaultValueData}
                onClose={() => setDefaultValueData(undefined)}
                onSubmit={onSaveSetDefault}
                defaultValues={defaultValues}
            />

            <NodeContext.Provider value={xyflow}>
                <div
                    className={styles.rfWrapper}
                    onClick={autocomplete.onDoubleClick}
                    style={{ height: "100%", width: "100%" }}
                    ref={renderReactFlow.ref}
                >
                    <ReactFlow
                        onDelete={() => xyflow.setActiveNodeId(undefined)}
                        colorMode={(theme?.includes("dark") ? "dark" : "light")}
                        onPaneClick={xyflow.onPaneClick}
                        proOptions={{ hideAttribution: true }}
                        nodes={xyflow.nodes}
                        edges={xyflow.edges}
                        onNodesChange={xyflow.onNodesChange}
                        onEdgesChange={xyflow.onEdgesChange}
                        onConnect={xyflow.onConnect}
                        onConnectEnd={onConnectEnd}
                        onEdgeClick={xyflow.onEdgeClick}
                        nodeTypes={{
                            customNode: CustomNode,
                        }}
                        key={activeProject?.id}
                        snapToGrid={true}
                        snapGrid={[20, 20]}
                        fitView
                        attributionPosition="bottom-left"
                    >
                        <ReactFlowContextProvider setZoomIn={setZoomIn} setZoomOut={setZoomOut} />
                        <Background />
                        <MiniMap />
                    </ReactFlow>
                </div>
            </NodeContext.Provider>
        </div>
    )
}

